import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AdsGETApiWithToken } from '../../../Constant/api';
import { slugs } from '../../../Constant/slugs';
import NotFound from '../../NotFound';
import FirstScreenExam from './FirstScreen';
import { FullScreen, useFullScreenHandle } from "react-full-screen";

const DB_NAME = "class_exam_browser_db";
const DB_VERSION = 1;
const STORE_NAME = "exam_cache";
const CACHE_KEY = "current_exam";
const CACHED_EXAM_SLUG_KEY = "class_exam_cached_slug";
const EXAM_LOAD_MODE_KEY = "class_exam_load_mode";
const nodejsurl = "https://real-exam.epsexam.com/base64";

const isServerUnavailable = (error) => {
  return !error?.response || error.response.status >= 500;
};

const openExamDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const getCachedExam = async () => {
  const db = await openExamDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).get(CACHE_KEY);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
};

const saveCachedExam = async (slug, examData) => {
  const db = await openExamDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put({
      key: CACHE_KEY,
      slug,
      examData,
      savedAt: Date.now(),
    });
    tx.oncomplete = () => {
      db.close();
      localStorage.setItem(CACHED_EXAM_SLUG_KEY, slug);
      resolve(true);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
};

const isRemoteUrl = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim().toLowerCase();
  return trimmed.startsWith("http://") || trimmed.startsWith("https://");
};

const inferMimeType = (url, mediaType, serverMime) => {
  if (serverMime && typeof serverMime === "string") return serverMime;

  const cleanUrl = String(url || "").split("?")[0].toLowerCase();
  if (mediaType === "audio") {
    if (cleanUrl.endsWith(".wav")) return "audio/wav";
    if (cleanUrl.endsWith(".ogg")) return "audio/ogg";
    if (cleanUrl.endsWith(".m4a")) return "audio/mp4";
    return "audio/mpeg";
  }

  if (cleanUrl.endsWith(".png")) return "image/png";
  if (cleanUrl.endsWith(".webp")) return "image/webp";
  if (cleanUrl.endsWith(".gif")) return "image/gif";
  if (cleanUrl.endsWith(".svg")) return "image/svg+xml";
  return "image/jpeg";
};

const blobToDataUrl = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const downloadAsDataUrl = async (url, mediaType) => {
  if (!isRemoteUrl(url)) return url;

  // First use the same proxy/base64 service already used by FirstScreen.js.
  try {
    const response = await fetch(`${nodejsurl}?url=${encodeURIComponent(url)}`);
    if (!response.ok) throw new Error(`Base64 server returned ${response.status}`);

    const result = await response.json();
    if (result?.success && result?.data) {
      const mime = inferMimeType(url, mediaType, result.mimeType || result.contentType);
      return `data:${mime};base64,${result.data}`;
    }
  } catch (error) {
    console.warn("Base64 proxy failed, trying direct media download:", url, error);
  }

  // Fallback works when the media host allows CORS.
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Media download returned ${response.status}`);
    return await blobToDataUrl(await response.blob());
  } catch (error) {
    console.warn("Could not cache media; keeping original URL:", url, error);
    return url;
  }
};

const normalizeMediaType = (value) => {
  const type = String(value || "").trim().toLowerCase();
  if (["image", "img", "photo", "picture"].includes(type)) return "image";
  if (["audio", "sound", "mp3"].includes(type)) return "audio";
  return type;
};

const prepareQuestionForOffline = async (question) => {
  const prepared = { ...question };
  const answerType = normalizeMediaType(question.answer_media_type);
  const descriptionType = normalizeMediaType(question.question_description_type);
  const mediaType = normalizeMediaType(question.question_media_type);

  if (answerType === "audio" || answerType === "image") {
    const fields = ["option_1", "option_2", "option_3", "option_4"];
    await Promise.all(fields.map(async (field) => {
      prepared[field] = await downloadAsDataUrl(question[field], answerType);
    }));
  }

  if (descriptionType === "audio" || descriptionType === "image") {
    prepared.question_description = await downloadAsDataUrl(
      question.question_description,
      descriptionType
    );
  }

  if (mediaType === "audio" || mediaType === "image") {
    prepared.question_media = await downloadAsDataUrl(
      question.question_media,
      mediaType
    );
  }

  return prepared;
};

const prepareExamForOffline = async (examData) => {
  const preparedExam = JSON.parse(JSON.stringify(examData));
  const rawQuestions = preparedExam?.meta?.exam_question_block_text;

  if (!rawQuestions) return preparedExam;

  const questions = JSON.parse(rawQuestions);
  const entries = await Promise.all(
    Object.entries(questions).map(async ([key, question]) => {
      return [key, await prepareQuestionForOffline(question)];
    })
  );

  preparedExam.meta.exam_question_block_text = JSON.stringify(
    Object.fromEntries(entries)
  );

  return preparedExam;
};

export default function StartClassExam(props) {
  const dispatch = useDispatch()
  const location = useLocation()
  const [ExamData, setAllExamList] = React.useState({});
  const [ExamFetch, setExamFetch] = React.useState(false);
  const [NotFoundData, setNotFound] = React.useState(false)
  const [reload, setReload] = React.useState(false)
  const [choose, setChoose] = React.useState(0)
  const [AnswerArray, setAnswerArray] = React.useState([]);
  const [ResultData, setResultData] = React.useState({meta:{exam_date_results:""}});
  const token = localStorage.getItem('teacher_token');

  const CachedExam = async (slug, allowPreviousOnServerError = false) => {
    try {
      const cached = await getCachedExam();
      const slugMatches = cached?.slug === slug;

      if (cached?.examData && (slugMatches || allowPreviousOnServerError)) {
        if (!slugMatches) {
          console.warn(
            `Server unavailable for ${slug}. Using previous cached exam ${cached.slug}.`
          );
        }
        setAllExamList(cached.examData);
        setNotFound(false);
        return true;
      }
    } catch (error) {
      console.error("IndexedDB read error:", error);
    }
    return false;
  };

  const fetchExam = async (slug) => {
    setExamFetch(false)
    setReload(true)
    setNotFound(false)

    const loadMode = sessionStorage.getItem(EXAM_LOAD_MODE_KEY);
    const shouldUseCacheFirst = loadMode === "cache";

    try {
      // Same featured-exam slug: do not download the exam again.
      if (shouldUseCacheFirst) {
        const found = await CachedExam(slug);
        if (found) return;
      }

      // Different slug (or cache missing): get the new exam from server.
      const res = await AdsGETApiWithToken(
        { slug: slug },
        slugs.db_slug_paid_exam,
        token
      );

      if (
        res.data.length === 1 &&
        res.data[0].meta.exam_question_block_student !== undefined
      ) {
        // Download image/audio into Base64 BEFORE saving. This makes the
        // IndexedDB copy usable when the exam server/media server is offline.
        const offlineReadyExam = await prepareExamForOffline(res.data[0]);

        await saveCachedExam(slug, offlineReadyExam);
        sessionStorage.setItem(EXAM_LOAD_MODE_KEY, "cache");

        setAllExamList(offlineReadyExam);
        setNotFound(false);
      } else {
        setNotFound(true);
      }
    } catch (error) {
      console.error("Exam server error:", error);

      // Only actual network/server failure uses the browser copy.
      // Authorization/client errors such as 401/403 do not use offline fallback.
      if (isServerUnavailable(error)) {
        // If the featured slug just changed but its fresh payload could not be
        // downloaded, use the last completely cached exam as emergency fallback.
        const found = await CachedExam(slug, true);
        if (!found) setNotFound(true);
      } else {
        setNotFound(true);
      }
    } finally {
      setExamFetch(true)
      setReload(false)
    }
  }

  const [finishStatus, setfinishStatus] = React.useState(false);
  const handle = useFullScreenHandle();

  useEffect(() => {
    var userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    }
    else
      handle.enter()

    fetchExam(location.pathname.slice(slugs.start_class_exam.length + 1))
  }, [])

  return (
    <div>
        <FullScreen handle={handle}>
          <div style={{minHeight: "100vh",backgroundColor:"#fff" }}>
            <div>
              {/* <div className="row">
            <div className="col-sm-2 col-1"></div>
            <div className="col-sm-8 col-10 form-view m-3"> */}
              <div className="form-view-2 m-3">
                {ExamFetch &&
                  <>
                    {NotFoundData ?
                      <NotFound /> :
                      <>
                        <FirstScreenExam
                          ExamData={ExamData}
                          setReload={setReload}
                          choose={choose}
                          setChoose={setChoose}
                          AnswerArray={AnswerArray}
                          setAnswerArray={setAnswerArray}
                          ResultData={ResultData} setResultData={setResultData}
                        />
                      </>
                    }
                  </>
                }
              </div>
              {/* </div> */}
            </div>
            {reload &&
              <div className="modal" style={{ padding: "50vw" }}>
                <div className="dot-pulse"></div>
              </div>
            }
          </div>
        </FullScreen>
      </div>
  )
}